import { Link } from "react-router-dom";
import Container from "components/container";
import {
  breakpoint,
  textColor,
  whiteColor,
  lightBlueColor,
  BasicSection,
} from "components/styles";
import styled from "@emotion/styled";

const Root = styled.div({
  paddingBottom: 32,
});

const Header = styled.header({
  paddingTop: 128,
  paddingBottom: 64,
  textAlign: "center",
  color: whiteColor,
  background: [
    // `linear-gradient(${darkBlueColor}cc,${darkBlueColor}cc)`,
    `url(${process.env.PUBLIC_URL}/static/media/housess.jpg) no-repeat center center / cover`,
  ].join(","),
});

const Title = styled.div({
  marginLeft: "auto",
  marginRight: "auto",
  fontSize: "4.75rem",
  lineHeight: "1",
  fontWeight: "500",
  [`@media (max-width: ${breakpoint}px)`]: {
    fontSize: "3rem",
  },
});

const EventYear = styled.div({
  marginBottom: -66,
  paddingTop: 64,
  fontSize: "14rem",
  fontWeight: "bold",
  opacity: 0.4,
  [`@media (max-width: ${breakpoint}px)`]: {
    marginBottom: -40,
    fontSize: "8rem",
  },
});

const EventCity = styled.div({
  fontSize: "3.2125rem",
  fontWeight: "500",
  textTransform: "uppercase",
  [`@media (max-width: ${breakpoint}px)`]: {
    fontSize: "1.84rem",
  },
});

const EventDate = styled.div({
  paddingTop: 16,
  paddingBottom: 32,
  fontSize: "2.5rem",
  fontWeight: "400",
  [`@media (max-width: ${breakpoint}px)`]: {
    fontSize: "1.5rem",
  },
});

type Panel1Props = {
  fontSize?: string;
  color?: "white";
};

const Panel1 = styled("section", {
  shouldForwardProp: (propName: string) =>
    propName !== "fontSize" && propName !== "color",
})(({ fontSize, color }: Panel1Props) => ({
  lineHeight: 1,
  fontWeight: "500",
  ...(fontSize ? { fontSize } : {}),
  ...(color === "white" ? { color: whiteColor } : { color: textColor }),
}));

const Section1 = styled.p({
  marginTop: 8,
});

const TwoButtonBlower = styled.div({
  display: "grid",
  flexDirection: "column",
  gap: 14,
  gridTemplateColumns: "1fr 1fr",
  maxWidth: "80%",
  margin: "48px auto 0",
  [`@media (max-width: ${breakpoint}px)`]: {
    gridTemplateColumns: "1fr",
    maxWidth: "100%",
  },
  "& > a": {
    display: "inline-flex",
    padding: "5px 12px",
    minHeight: 75,
    fontSize: "1.6rem",
    textDecoration: "underline",
    backgroundColor: "#fafafa",
    color: "#39a4fc",
    borderRadius: 5,
    border: "1px solid #e3e3e3",
    textAlign: "center",
    transition: "background 0.3s ease",
    alignItems: "center",
    justifyContent: "center",
    fontStyle: "italic",
    fontWeight: 500,
    lineHeight: 1,
    "&:hover": {
      backgroundColor: "#39a4fc",
      color: "#fafafa",
    },
    [`@media (max-width: ${breakpoint}px)`]: {
      minHeight: 65,
      fontSize: "1.4rem",
    },
  },
});

const RegistrationFeeTitle = styled.div({
  marginBottom: 16,
  fontSize: "1.8rem",
});

const RegistrationFeeContainer = styled.ul({
  display: "flex",
  flexDirection: "column",
  rowGap: 8,
});

const RegistrationFeeItem = styled.li({
  marginLeft: 24,
});

const MessageBar = styled.div({
  textAlign: "center",
  fontWeight: "bold",
  fontSize: "2.813rem",
  lineHeight: "2.8rem",
  [`@media (max-width: ${breakpoint}px)`]: {
    fontSize: "1.8rem",
    lineHeight: "1.9rem",
  },
});

const InquiryBar = styled.div({
  textAlign: "center",
  color: "#344961",
  fontSize: "1.6rem",
  lineHeight: "2.5rem",
  fontWeight: 500,
  "& > a": {
    textDecoration: "underline",
    color: "#39a4fc",
    fontWeight: "bold",
  },
});

const CardsTitle = styled.h2({
  textAlign: "center",
  fontWeight: "bold",
  "& > :first-of-type": {
    fontSize: "2.25rem",
  },
  "& > :last-of-type": {
    fontSize: "2.5rem",
  },
});

const ImageSection = styled.section({
  height: 256,
  background: [
    `linear-gradient(${lightBlueColor}99,${lightBlueColor}99)`,
    `url(${process.env.PUBLIC_URL}/static/media/building.jpg) no-repeat center center / cover`,
  ].join(","),
});

const UnorderedList = styled.ul({
  paddingLeft: 22,
  marginTop: 24,
  "& > li": {
    marginTop: 24,
    "&:first-of-type": {
      marginTop: 0,
    },
    "& > a": {
      textDecoration: "underline",
      color: "#39a4fc",
      "&:hover": {
        color: "#344961",
      },
      wordBreak: "break-all",
    },
  },
});

const Conference = styled.div({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: 10,
  [`@media (max-width: ${breakpoint}px)`]: {
    gridTemplateColumns: "1fr",
  },
  "& > :first-of-type > div, & > :last-of-type > div": {
    display: "flex",
    justifyContent: "flex-start",
    gap: 10,
    fontSize: "1.375rem",
    fontWeight: 400,
    "& > span": {
      color: "#8c98aa",
      fontWeight: 300,
    },
  },
  "& > :first-of-type > div": {
    lineHeight: "1.6rem",
    marginBottom: 10,
    "& > span": {
      fontStyle: "italic",
      flex: "0 0 145px",
    },
  },
  "& > :last-of-type > div": {
    lineHeight: "2rem",
    "& > span": {
      fontStyle: "normal",
      fontSize: "1.375rem",
      lineHeight: "2rem",
      flex: "0 0 70px",
      [`@media (max-width: ${breakpoint}px)`]: {
        flex: "0 0 135px",
      },
    },
  },
});

const LinkBar = styled.div({
  textAlign: "center",
  display: "grid",
  gridTemplateColumns: "1fr ",
  maxWidth: "30%",
  margin: "0  auto",
  gap: 30,
  [`@media (max-width: ${breakpoint}px)`]: {
    maxWidth: "100%",
    gridTemplateColumns: " 1fr",
    gap: 10,
  },
});

const LinkButton = styled.a({
  display: "inline-block",
  minWidth: 240,
  padding: 16,
  borderRadius: 8,
  textAlign: "center",
  fontWeight: 500,
  fontSize: "1.5rem",
  lineHeight: "inherit",
  color: "white",
  transition: "0.3s ease",
  backgroundColor: "#344961",
  textDecoration: "none",
  "&:hover": {
    backgroundColor: "#647692",
  },
});

const LinkList = styled.ul({
  display: "flex",
  flexWrap: "wrap",
  maxWidth: "58%",
  justifyContent: "space-evenly",
  alignItems: "center",
  gap: 32,
  marginLeft: "auto",
  marginRight: "auto",
  [`@media (max-width: ${breakpoint}px)`]: {
    maxWidth: "100%",
    gap: "0",
  },
});

const LinkItem = styled.li({
  listStyleType: "none",
});

type ImageProps = {
  padding?: "compact";
};

const NexusImage = styled("img", {
  shouldForwardProp: (propName: string) => propName !== "padding",
})(({ padding }: ImageProps) => ({
  height: padding === "compact" ? 90 : 120,
  objectPosition: "0 1px",
  [`@media (max-width: ${breakpoint}px)`]: {
    height: padding === "compact" ? 67.5 : 90,
    objectPosition: "0 -5.25px",
  },
}));

const CboeImage = styled("img", {
  shouldForwardProp: (propName: string) => propName !== "padding",
})(({ padding }: ImageProps) => ({
  height: padding === "compact" ? 45 : 60,
  [`@media (max-width: ${breakpoint}px)`]: {
    height: padding === "compact" ? 33.75 : 45,
  },
}));

const Paragraph = styled.p<{ AlignCenter?: string }>`
  text-align: ${({ AlignCenter }) =>
    AlignCenter === "center" ? "center" : "left"};
`;

const DateLocation = styled(Paragraph)({
  lineHeight: 1.1,
});

const isFirstEarlyBird = new Date() < new Date("2025-06-28");

const EBIG2025 = () => (
  <Root>
    <Header>
      <Container>
        <Title>
          Equity-Based
          <br />
          Insurance Guarantee
          <br />
          Conference
        </Title>
        <EventYear>2025</EventYear>
        <EventCity>Cboe in Chicago</EventCity>
        <EventDate>Nov 17-18</EventDate>
        <LinkList>
          <LinkItem>
            <Link to="/">
              <NexusImage
                src={`${process.env.PUBLIC_URL}/static/media/nexus-light.png`}
                alt="Nexus Risk Management"
              />
            </Link>
          </LinkItem>
          <LinkItem>
            <a
              href="https://www.cboe.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <CboeImage
                src={`${process.env.PUBLIC_URL}/static/media//cboe-light.png`}
                alt="Cboe"
              />
            </a>
          </LinkItem>
        </LinkList>
      </Container>
    </Header>
    <BasicSection>
      <Container>
        <Panel1 fontSize="1.6rem">
          <p>
            <b>
              The Equity-Based Insurance Guarantee Conference returns to Cboe in
              Chicago on November 17-18, 2025.
            </b>
          </p>
          <Section1>
            The 2024 conference set a high standard, attracting enthusiastic
            participants from major annuity writers, leading investment banks,
            actuarial consulting firms, and software providers.
          </Section1>
          <Section1>
            Bringing together industry leaders from insurance, banking, and
            financial markets, EBIG remains the premier event for connecting
            participants from the buy side and sell side of the street,
            providing unmatched opportunities for professional growth,
            networking, and staying on top of the latest industry developments.
          </Section1>
          <Section1>
            Whether you're an actuary or a non-actuary, this event provides the
            perfect platform to exchange ideas, expand your network, and gain
            valuable insights into index-linked annuities, risk management,
            reserving, and capital issues.
          </Section1>
          <TwoButtonBlower>
            <a
              href="https://cboeglobalmarkets.smugmug.com/EBIGOPO11182024/n-5jRnck"
              target="_blank"
              rel="noopener noreferrer"
            >
              EBIG in Pictures
            </a>
            <a
              href="https://www.linkedin.com/posts/cboe_the-2024-equity-based-insurance-guarantee-activity-7264744396443574274-Dh5s/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Opening Bell Ringing with EBIG Sponsors
            </a>
          </TwoButtonBlower>
        </Panel1>
      </Container>
    </BasicSection>
    <BasicSection padding="compact" gap="compact" background="dark-blue">
      <Container>
        <Panel1 fontSize="2.5rem" color="white">
          <Paragraph AlignCenter="center">We hope you will join us!</Paragraph>
        </Panel1>
      </Container>
    </BasicSection>
    <ImageSection />
    <BasicSection gap="compact" background="dark-blue">
      <Container>
        <Panel1 color="white">
          <CardsTitle>
            <div>Equity-Based Insurance Guarantee Conference</div>
          </CardsTitle>
        </Panel1>
        <Panel1 fontSize="2.063rem" color="white">
          <DateLocation AlignCenter="center">
            November 17-18, 2025
            <br />
            Chicago, IL
          </DateLocation>
        </Panel1>
        <Panel1 fontSize="1.3rem" color="white">
          <Conference>
            <div>
              <div>
                <span>Presented by:</span> Nexus Risk Management
              </div>
              <div>
                <span>Sponsored by:</span> Cboe Global Markets
              </div>
              <div>
                <span>Chairperson:</span> Dr. Kannoo (Ravi) Ravindran
              </div>
            </div>
            <div>
              <div>
                <span>Venue:</span>The Marquee Conference Center <br /> The Old
                Post Office (New Cboe Headquarters)
                <br />
                433 W. Van Buren Street, Chicago, IL 60607
              </div>
            </div>
          </Conference>
        </Panel1>
      </Container>
    </BasicSection>
    <BasicSection>
      <Container>
        <Panel1 fontSize="1.6rem">
          <p>
            For your convenience, we have listed a few nearby hotels within
            walking distance of the Cboe Headquarters in Chicago. Please note
            that attendees are welcome to book accommodations at any hotel of
            their choice.
          </p>
          <UnorderedList>
            <li>
              The LaSalle Chicago, Autograph Collection{" "}
              <a
                href="https://www.thelasallechicago.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://www.thelasallechicago.com/
              </a>
            </li>
            <li>
              JW Marriott Chicago{" "}
              <a
                href="https://www.marriott.com/CHIJW"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://www.marriott.com/CHIJW
              </a>
            </li>
            <li>
              Kimpton Gray Hotel{" "}
              <a
                href="https://www.grayhotelchicago.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://www.grayhotelchicago.com/
              </a>
            </li>
          </UnorderedList>
        </Panel1>
      </Container>
    </BasicSection>
    <BasicSection padding="compact" gap="compact" background="dark-blue">
      <Container>
        <Panel1 fontSize="1.6rem" color="white">
          <RegistrationFeeTitle>Registration Fee</RegistrationFeeTitle>
          <RegistrationFeeContainer>
            <RegistrationFeeItem>Standard Fee: $1,600</RegistrationFeeItem>
            <RegistrationFeeItem>
              Early Bird Fee: ${isFirstEarlyBird ? "1,250" : "1,450"} (Register
              before {isFirstEarlyBird ? "June 28" : "October 18"} to save $
              {isFirstEarlyBird ? 350 : 150}!)
            </RegistrationFeeItem>
          </RegistrationFeeContainer>
          <p>Secure Your Spot Today and Save Big with Early Bird Pricing!</p>
        </Panel1>
      </Container>
    </BasicSection>
    <BasicSection padding="compact" gap="compact" background="light-blue">
      <Container>
        <Panel1>
          <MessageBar>See you at the Exchange!</MessageBar>
        </Panel1>
      </Container>
    </BasicSection>
    <BasicSection padding="compact" gap="compact">
      <Container>
        <Panel1>
          <LinkBar>
            <LinkButton
              href="https://cvent.me/mvEOZX"
              target="_blank"
              rel="noopener noreferrer"
            >
              Register
            </LinkButton>
            <LinkButton
              href="/documents/ebig/2024/EBIG Agenda.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              Program Agenda
            </LinkButton>
          </LinkBar>
        </Panel1>
        <InquiryBar>
          For more information and sponsorship inquiries please contact <br />
          <a href="mailto:inquiries@nexusrisk.com">inquiries@nexusrisk.com</a>
        </InquiryBar>
      </Container>
    </BasicSection>
  </Root>
);

export default EBIG2025;
